import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Shield, FileText, Database, TrendingUp, Globe, Phone } from 'lucide-react';
import SEO, { createLocalBusinessSchema } from '../components/SEO';

const Home = () => {
  const localBusinessSchema = createLocalBusinessSchema();
  const categories = [
    {
      title: 'Spices',
      image: 'https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?w=800',
      description: 'Whole, ground & blends with consistent specifications',
      link: '/products/spices'
    },
    {
      title: 'Edible Oils & Fats',
      image: 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/d151e52bf6aaacd516678042204e23df8a464c8de5c39856e41dea329e3c649f.png',
      description: 'Refined and blended supply with spec-based sourcing',
      link: '/products/edible-oils'
    },
    {
      title: 'Sugar',
      image: 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/66445efc0d828bce8a60cb23e72e1e2edffe3626719afecc490acb033d435a63.png',
      description: 'Bulk supply for distributors and industrial buyers',
      link: '/products/sugar'
    },
    {
      title: 'Cereals, Pulses & Grains',
      image: 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/7527556706218bef4e66989a625b238683623d4e466abbc1e1eee0e3576a5400.png',
      description: 'Premium Basmati, Non-Basmati rice, pulses & millets',
      link: '/products/grains'
    },
    {
      title: 'Fruits & Vegetables',
      image: 'https://static.prod-images.emergentagent.com/jobs/71d5fd02-4819-4dfa-92cf-0a0a2bf388cc/images/bc8824e58cd3f0cd5e99fdea2ca9eecc649ef76f764289be97e3bc4aa0fcb944.png',
      description: 'Fresh and processed options with cold-chain support',
      link: '/products/produce'
    },
    {
      title: 'Dehydrated Aromatics',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/cd882a8944ead0a49d53ba82cabd72a907092f0b14e407f2fa4381bad34b77e7.png',
      description: 'Premium dehydrated powders: Moringa, Onion, Garlic, Ginger',
      link: '/products/dehydrated-aromatics'
    },
    {
      title: 'Dry Fruits',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/ee20ff0401cdf86b7989f0ceed5e5fc26a265f99fa000464050ad1a0453ec047.png',
      description: 'Premium cashews, almonds & dry fruits for retail & HoReCa',
      link: '/products/dry-fruits'
    },
    {
      title: 'ORIVANA Wellness',
      image: 'https://static.prod-images.emergentagent.com/jobs/eb4529a2-4ca6-41dd-a194-af190196c3ae/images/24de089d2fe994bf370732b0f01b55bff434c81030e30910a7c40d9be8baca51.png',
      description: 'Ayurvedic supplements, herbal teas & wellness products',
      link: '/products/wellness'
    }
  ];

  const problems = [
    'Spec drift across lots → claims, rejections, loss of repeat orders',
    'Inconsistent QA evidence → buyer trust gaps',
    'Missing/incorrect documents → customs delays and demurrage risk',
    'Counterparty uncertainty → fraud/payment and performance risk'
  ];

  const solutions = [
    {
      icon: <Shield className="w-12 h-12 text-[#263776]" />,
      title: 'Verified Sourcing',
      description: 'Repeatable sourcing lanes with supplier discipline and lot identity'
    },
    {
      icon: <CheckCircle2 className="w-12 h-12 text-[#263776]" />,
      title: 'QA Gates',
      description: 'Sampling workflow, COA capture, and spec checks for consistent quality'
    },
    {
      icon: <FileText className="w-12 h-12 text-[#263776]" />,
      title: 'Export-Ready Pack',
      description: 'Document checklists and completeness review by destination/product'
    }
  ];

  const workflow = [
    'Capture requirement (spec, destination, packaging, quantity)',
    'Verify counterparties (for new relationships)',
    'Sourcing plan + lot creation',
    'QA gates (sampling + COA + spec checks)',
    'Warehousing/cold chain readiness',
    'Export pack review + completeness checks',
    'Dispatch + dossier archive + performance scorecard'
  ];

  return (
    <div className="w-full">
      <SEO 
        title="SPANXPORT - Powering Intelligent Global Trade | Premium Indian Exports"
        description="SPANXPORT is your trusted partner for premium Indian agricultural exports. Spices, dry fruits, grains, oils, fresh produce - sourced with quality, delivered with trust. EximStack powered traceability."
        keywords="Indian exports, spices export, dry fruits, grapes export, mango export, B2B trade, agricultural commodities, SPANXPORT, global trade"
        url="/"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <section
        className="relative h-[600px] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/eb4529a2-4ca6-41dd-a194-af190196c3ae/images/8a5f50eaee265b4ec79cbad6b880ec6af706266ae23072934b889f77951dc83e.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="block text-6xl md:text-7xl mb-2">SPANXPORT</span>
          </h1>
          <p className="text-2xl md:text-3xl font-semibold mb-4 text-[#F47D20]">
            From Farm to World — Your Global Commodity Partner
          </p>
          <p className="text-lg md:text-xl mb-8 text-gray-200">
            Premium spices, grains, oils, sugar & produce — sourced with quality, delivered with trust.
          </p>
          <p className="text-base md:text-lg mb-8 text-gray-300">
            Powered by EximStack: Traceability + QA Gates + Export Documentation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/verified-directory">Join Verified Directory</Link>
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
            <span>Africa</span>
            <span>•</span>
            <span>Asia-Pacific</span>
            <span>•</span>
            <span>GCC</span>
            <span>•</span>
            <span>LATAM</span>
            <span>•</span>
            <span>South Asia</span>
            <span>•</span>
            <span>UK & EU</span>
            <span>•</span>
            <span>US & Canada</span>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-slate-100 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-slate-700">
            <span className="font-medium">Bulk Supply</span>
            <span>•</span>
            <span className="font-medium">Private Label</span>
            <span>•</span>
            <span className="font-medium">Documentation Support</span>
            <span>•</span>
            <span className="font-medium">Cold Chain Options</span>
          </div>
        </div>
      </section>

      {/* Core Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Core Categories</h2>
            <p className="text-lg text-slate-600">Export-ready lots, consistent specifications, disciplined documentation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <Link key={index} to={category.link}>
                <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden h-full cursor-pointer">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-[#F47D20] transition-colors">{category.title}</h3>
                    <p className="text-slate-600">{category.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild size="lg">
              <Link to="/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">Why Commodity Shipments Fail at Scale</h2>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-sm">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-lg text-slate-700">{problem}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">The SPANXPORT Solution</h2>
            <p className="text-xl text-slate-600">Verified supply. Buyer-ready execution.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {solutions.map((solution, index) => (
              <Card key={index} className="text-center p-8 hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">{solution.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                <p className="text-slate-600">{solution.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* EximStack Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">EximStack</h2>
            <p className="text-xl text-slate-600 mb-8">A shipment-level operating system that keeps every order buyer-ready</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Database className="w-10 h-10 text-[#263776] mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Traceability Dossier</h3>
                <p className="text-sm text-slate-600">Lot/batch identity + evidence vault</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <CheckCircle2 className="w-10 h-10 text-[#F47D20] mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">QA Gates</h3>
                <p className="text-sm text-slate-600">Sampling, COA capture, lot scorecards</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <FileText className="w-10 h-10 text-[#263776] mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Export Pack</h3>
                <p className="text-sm text-slate-600">Templates + checklists + completeness checks</p>
              </div>
            </div>
            <Button asChild size="lg" className="bg-[#263776] text-white hover:bg-[#1e2a5e]">
              <Link to="/eximstack">Learn More About EximStack</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Verified Directory Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">Verified Directory</h2>
            <p className="text-xl text-slate-600 mb-8">International database of verifiable buyers & sellers</p>
            <p className="text-lg text-slate-700 mb-8">
              A permissioned directory to reduce fraud, onboarding time, and unreliable lanes—built for repeat trade relationships.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3">What's Inside</h3>
                <ul className="text-left space-y-2 text-slate-600">
                  <li>• KYC + credentials vault (permissioned)</li>
                  <li>• Trade references + lane history</li>
                  <li>• Performance scoring</li>
                  <li>• Matchmaking for repeat lanes</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3">Benefits</h3>
                <ul className="text-left space-y-2 text-slate-600">
                  <li>• Reduce fraud and payment risk</li>
                  <li>• Faster counterparty verification</li>
                  <li>• Build repeatable trade lanes</li>
                  <li>• Performance transparency</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/verified-directory">Apply as Buyer</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/verified-directory">Apply as Seller</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">From Requirement to Shipment in 7 Steps</h2>
            <div className="space-y-4">
              {workflow.map((step, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-sm">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-lg text-slate-700 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Build a Repeatable Trade Lane with SPANXPORT</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Share commodity + spec + quantity + destination—receive a structured quote and shipment checklist
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/gulfood">Book a Meeting (Gulfood)</Link>
            </Button>
          </div>
          <div className="space-y-2">
            <p>
              <a href="mailto:admin@spanxport.com" className="hover:underline">admin@spanxport.com</a>
            </p>
            <p>
              <a href="https://wa.me/918291234503" className="hover:underline">WhatsApp: +91-829-12345-03</a>
              {' '}
              <span className="text-slate-300">|</span>
              {' '}
              <a href="https://wa.me/6598635879" className="hover:underline">+65-986-35879</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
